import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button, Modal, Form, Offcanvas } from 'react-bootstrap';
import ReactPaginate from 'react-paginate';
import { useAuth } from './../../context/AuthContext';
import withPermission from './../../utils/withPermission';
import Swal from 'sweetalert2';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const API_URL = `${backendUrl}/api/activity-types`;

const ActivityTypeList = () => {
    const [activityTypes, setActivityTypes] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({ name: '', description: '' });
    const [isEdit, setIsEdit] = useState(false);
    const [selectedType, setSelectedType] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageCount, setPageCount] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const WithListPermission = withPermission(({ children }) => children, ['activitytype-list']);
    const WithCreatePermission = withPermission(({ children }) => children, ['activitytype-create']);
    const WithDeletePermission = withPermission(({ children }) => children, ['activitytype-delete']);
    const WithEditPermission = withPermission(({ children }) => children, ['activitytype-edit']);

    const debounceTimeoutRef = useRef(null);

    useEffect(() => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(() => {
            fetchActivityTypes(1, searchQuery);
        }, searchQuery ? 500 : 0);

        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, [searchQuery]);

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const fetchActivityTypes = async (page = 1) => {
        setLoading(true);
        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;
            const response = await axios.get(`${API_URL}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                params: {
                    page,
                    search: searchQuery,
                },
            });
            setActivityTypes(response.data?.data || []);
            setPageCount(response.data.last_page || 1);
            setCurrentPage(response.data.current_page || 1);
        } catch (error) {
            console.error('Error fetching activity types:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddType = () => {
        setFormData({ name: '', description: '' });
        setIsEdit(false);
        setSelectedType(null);
        setShowModal(true);
    };

    const handleEditType = (type) => {
        setFormData({
            name: type.name,
            description: type.description || ''
        });
        setIsEdit(true);
        setSelectedType(type);
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!"
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const userInfo = JSON.parse(localStorage.getItem('user-info'));
                    const origin = localStorage.getItem('user-origin');
                    const token = userInfo?.token;
                    await axios.delete(`${API_URL}/${id}`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Tenant-Origin': origin,
                        },
                    });
                    setActivityTypes(activityTypes.filter(type => type.id !== id));
                    Swal.fire("Deleted!", "Activity type has been deleted.", "success");
                } catch (error) {
                    console.error('Error deleting activity type:', error);
                    Swal.fire("Error!", "Failed to delete activity type.", "error");
                }
            }
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: "",
        }));
    };

    const handlePageChange = ({ selected }) => {
        const newPage = selected + 1;
        setCurrentPage(newPage);
        fetchActivityTypes(newPage);
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name?.trim()) {
            newErrors.name = 'Name is required';
        }
        return newErrors;
    };

    const handleSubmit = async () => {
        const userInfo = JSON.parse(localStorage.getItem('user-info'));
        const origin = localStorage.getItem('user-origin');
        const token = userInfo?.token;
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        try {
            if (isEdit) {
                const response = await axios.put(`${API_URL}/${selectedType.id}`, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    },
                });
                setActivityTypes(activityTypes.map(type => (type.id === selectedType.id ? response.data.data : type)));
                Swal.fire("Updated!", "Activity type has been updated.", "success");
            } else {
                const response = await axios.post(API_URL, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    },
                });
                setActivityTypes([...activityTypes, response.data.data]);
                Swal.fire("Saved!", "Activity type has been created.", "success");
            }
            setShowModal(false);
            fetchActivityTypes(currentPage);
        } catch (error) {
            console.error('Error saving activity type:', error);
            Swal.fire("Error!", "Failed to save activity type.", "error");
        }
    };

    return (
        <div className="main-content">
            <div className="page-content">
                <div className="container-fluid ">
                    <div className="row w-100">
                        <div className="col-12">
                            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                <h4 className="mb-sm-0 font-size-18">Activity Type Setup</h4>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between mb-3">
                                        <div className="d-flex">
                                            <div className="search-box me-2 mb-2 d-inline-block">
                                                <div className="position-relative">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Search..."
                                                        value={searchQuery}
                                                        onChange={handleSearchChange}
                                                    />
                                                    <i className="bx bx-search-alt search-icon" />
                                                </div>
                                            </div>
                                        </div>
                                        <WithCreatePermission>
                                            <button
                                                type="button"
                                                className="btn rounded-circle border d-flex align-items-center justify-content-center round-create-button"
                                                onClick={handleAddType}
                                                data-bs-toggle="tooltip"
                                                data-bs-placement="top"
                                                title='Add Activity Type'
                                            >
                                                <i className="mdi mdi-plus" ></i>
                                            </button>
                                        </WithCreatePermission>
                                    </div>
                                    <table className="table align-middle table-nowrap table-check table-list">
                                        <thead className="table-light">
                                            <tr>
                                                <th style={{ width: '20px' }} className="align-middle">#</th>
                                                <th className="align-middle">Name</th>

                                                <th className="align-middle">Description</th>
                                                <th className="align-middle">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {activityTypes.map((type, index) => (
                                                <tr key={type.id}>
                                                    <td>{(currentPage - 1) * 10 + index + 1}</td>
                                                    <td>{type.name}</td>
                                                    <td>{type.description}</td>
                                                    <td>
                                                        <div className="dropdown">
                                                            <i
                                                                className="mdi mdi-dots-horizontal font-size-18"
                                                                data-bs-toggle="dropdown"
                                                                aria-expanded="false"
                                                                style={{ cursor: 'pointer' }}
                                                            ></i>
                                                            <ul className="dropdown-menu dropdown-menu-end font-size-14">
                                                                <li>
                                                                    <WithEditPermission>
                                                                        <button className="dropdown-item" onClick={() => handleEditType(type)}>
                                                                            Edit
                                                                        </button>
                                                                    </WithEditPermission>
                                                                </li>
                                                                <li>
                                                                    <WithDeletePermission>
                                                                        <button className="dropdown-item" onClick={() => handleDelete(type.id)}>
                                                                            Delete
                                                                        </button>
                                                                    </WithDeletePermission>
                                                                </li>
                                                            </ul>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    <div className="d-flex justify-content-end pagination-sm">
                                        <ReactPaginate
                                            previousLabel={'<'}
                                            nextLabel={'>'}
                                            breakLabel={'...'}
                                            pageCount={pageCount}
                                            marginPagesDisplayed={2}
                                            pageRangeDisplayed={3}
                                            onPageChange={handlePageChange}
                                            containerClassName={'pagination pagination-rounded justify-content-end mb-2'}
                                            pageClassName={'page-item'}
                                            pageLinkClassName={'page-link'}
                                            previousClassName={'page-item previous'}
                                            previousLinkClassName={'page-link'}
                                            nextClassName={'page-item next'}
                                            nextLinkClassName={'page-link'}
                                            breakClassName={'page-item'}
                                            breakLinkClassName={'page-link'}
                                            activeClassName={'active'}
                                            forcePage={currentPage - 1}
                                        />
                                    </div>

                                    <Offcanvas show={showModal} onHide={() => setShowModal(false)} placement='end'>
                                        <Offcanvas.Header closeButton>
                                            <Offcanvas.Title>{isEdit ? 'Edit Activity Type' : 'Add Activity Type'}</Offcanvas.Title>
                                        </Offcanvas.Header>
                                        <hr style={{ margin: 0 }} /><br />
                                        <Offcanvas.Body>
                                            <Form>
                                                <Form.Group className='mb-3'>
                                                    <Form.Label style={{ fontSize: "13px" }}>Name</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        style={{ fontSize: "13px" }}
                                                        name="name"
                                                        value={formData.name}
                                                        onChange={handleInputChange}
                                                        className={errors.name ? 'form-control is-invalid' : 'form-control'}
                                                    />
                                                    {errors.name && <div className="invalid-feedback">*{errors.name}</div>}
                                                </Form.Group>

                                                <Form.Group className='mb-3'>
                                                    <Form.Label style={{ fontSize: "13px" }}>Description</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        style={{ fontSize: "13px" }}
                                                        name="description"
                                                        value={formData.description}
                                                        onChange={handleInputChange}
                                                        className={errors.description ? 'form-control is-invalid' : 'form-control'}
                                                    />
                                                    {errors.description && <div className="invalid-feedback">*{errors.description}</div>}
                                                </Form.Group>
                                            </Form>
                                        </Offcanvas.Body>
                                        <div className="p-3 d-flex justify-content-end border-top">
                                            <Button variant="secondary me-2" onClick={() => setShowModal(false)}>Close</Button>
                                            <Button variant="primary" onClick={handleSubmit}>{isEdit ? 'Update' : 'Save'} </Button>
                                        </div>
                                    </Offcanvas>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ActivityTypeList;
